/**
 * The app's domain. This build is a HABIT TRACKER — the reference composition the real app replaces.
 *
 * Reshape the domain freely (a run log, an expense, a recipe, a client). What must survive the
 * reshape is the shape of the thinking:
 *  - one serialisable record with an `id`, persisted verbatim by `useLocalCollection`
 *  - derived values computed from that record, never stored alongside it (a streak is a function of
 *    the completion history, so it can never disagree with it)
 *  - dates keyed as plain local day strings, so a record means the same thing across time zones
 */

export interface Habit {
	id: string;
	name: string;
	/** Brand-independent accent index; the palette maps it to a real colour. */
	tone: number;
	createdAt: number;
	/** Local day keys ("2026-08-28") on which this habit was completed. */
	done: string[];
}

export const HABITS_KEY = 'habits';

/** Local day key. Deliberately NOT toISOString — that shifts the day for anyone west of UTC. */
export function dayKey(date: Date = new Date()): string {
	const y = date.getFullYear();
	const m = `${date.getMonth() + 1}`.padStart(2, '0');
	const d = `${date.getDate()}`.padStart(2, '0');
	return `${y}-${m}-${d}`;
}

/** The last `count` days, oldest first — the week strip on the home screen. */
export function recentDays(count = 7): { key: string; label: string; isToday: boolean }[] {
	const today = dayKey();
	return Array.from({ length: count }, (_, i) => {
		const date = new Date();
		date.setDate(date.getDate() - (count - 1 - i));
		const key = dayKey(date);
		return {
			key,
			label: date.toLocaleDateString(undefined, { weekday: 'narrow' }),
			isToday: key === today,
		};
	});
}

export const isDoneOn = (habit: Habit, key: string): boolean => habit.done.includes(key);
export const isDoneToday = (habit: Habit): boolean => isDoneOn(habit, dayKey());

/**
 * Consecutive days ending today (or yesterday, if today is not done yet).
 *
 * Not breaking the streak the moment midnight passes is the whole point: a tracker that shows "0"
 * at 00:01 on a day you have not started punishes people for sleeping, and they delete it.
 */
export function streakOf(habit: Habit): number {
	const done = new Set(habit.done);
	const cursor = new Date();
	if (!done.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);

	let streak = 0;
	while (done.has(dayKey(cursor))) {
		streak++;
		cursor.setDate(cursor.getDate() - 1);
	}
	return streak;
}

/** Longest run of consecutive days ever recorded — the number worth being proud of. */
export function bestStreakOf(habit: Habit): number {
	if (habit.done.length === 0) return 0;
	const sorted = [...habit.done].sort();
	let best = 1;
	let run = 1;
	for (let i = 1; i < sorted.length; i++) {
		const previous = new Date(sorted[i - 1]);
		previous.setDate(previous.getDate() + 1);
		if (dayKey(previous) === sorted[i]) run++;
		else run = 1;
		if (run > best) best = run;
	}
	return best;
}

/** Share of today's habits already completed, 0-1. Drives the ring on the home header. */
export function todayProgress(habits: Habit[]): number {
	if (habits.length === 0) return 0;
	return habits.filter(isDoneToday).length / habits.length;
}
